import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@h2/ui';
import { Button } from '@h2/ui';
import { Badge } from '@h2/ui';
import { ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { useConfigurationStore } from '../../stores/useConfigurationStore';
import { ConfigurationSection } from './ConfigurationSection';

interface ConfigurationStepperProps {
  onComplete?: () => void;
  completeButtonText?: string;
}

export const ConfigurationStepper: React.FC<ConfigurationStepperProps> = ({
  onComplete,
  completeButtonText = "완료"
}) => {
  const {
    activeConfiguration,
    currentStep,
    completedSteps,
    setCurrentStep,
    markStepCompleted,
    validateSection
  } = useConfigurationStore();

  if (!activeConfiguration) {
    return <div>설정을 불러오는 중...</div>;
  }

  const sections = activeConfiguration.sections;
  const currentSection = sections[currentStep];
  const canProceed = validateSection(currentSection.id);
  const isLastStep = currentStep === sections.length - 1;

  const handleNext = () => {
    if (canProceed) {
      markStepCompleted(currentStep);
      if (currentStep < sections.length - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        // 마지막 단계에서는 완료 함수 호출
        onComplete?.();
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (stepIndex: number) => {
    // 이전 스텝들이 완료되었거나 현재 스텝이면 이동 가능
    const canNavigate = stepIndex <= currentStep || completedSteps.includes(stepIndex);
    if (canNavigate) {
      setCurrentStep(stepIndex);
    }
  };

  return (
    <div className="space-y-4">
      {/* 스텝 네비게이션 - 슬림 버전 */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            {sections.map((section, index) => (
              <div key={section.id} className="flex items-center">
                <button
                  onClick={() => handleStepClick(index)}
                  className={`flex items-center justify-center w-8 h-8 rounded-full border-2 transition-colors ${
                    completedSteps.includes(index)
                      ? 'border-green-500 text-green-500 bg-white'
                      : index === currentStep
                      ? 'border-blue-500 text-blue-500'
                      : index < currentStep
                      ? 'border-gray-300 text-gray-500 hover:border-gray-400'
                      : 'border-gray-200 text-gray-400'
                  }`}
                  disabled={index > currentStep && !completedSteps.includes(index)}
                >
                  {completedSteps.includes(index) ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <span className="text-sm">{index + 1}</span>
                  )}
                </button>
                
                <div className="ml-2 text-left">
                  <div className={`text-sm font-medium ${
                    index === currentStep ? 'text-blue-600' : 'text-gray-700'
                  }`}>
                    {section.title}
                  </div>
                  {section.description && (
                    <div className="text-xs text-gray-500 mt-0.5">
                      {section.description}
                    </div>
                  )}
                </div>
                
                {index < sections.length - 1 && (
                  <div className="mx-4 h-px bg-gray-200 flex-1" />
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* 현재 섹션 */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                {currentSection.title}
                <Badge variant="outline">
                  {currentStep + 1} / {sections.length}
                </Badge>
              </CardTitle>
              {currentSection.description && (
                <CardDescription className="mt-2">
                  {currentSection.description}
                </CardDescription>
              )}
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          <ConfigurationSection section={currentSection} hideTitle={true} />
        </CardContent>

        {/* 네비게이션 버튼 */}
        <CardContent className="border-t bg-gray-50/50 pt-4">
          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={currentStep === 0}
              className="flex items-center gap-2"
            >
              <ChevronLeft className="w-4 h-4" />
              이전
            </Button>

            <div className="flex items-center gap-2">
              {!canProceed && (
                <span className="text-sm text-red-500">
                  모든 필수 항목을 입력해주세요
                </span>
              )}
              
              <Button
                onClick={handleNext}
                disabled={!canProceed}
                variant={isLastStep ? "default" : "outline"}
                className="flex items-center gap-2"
              >
                {isLastStep ? completeButtonText : "다음"}
                {!isLastStep && <ChevronRight className="w-4 h-4" />}
                {isLastStep && <Check className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
